import React from "react";
import { EquipmentDocumentI } from "../../../../libs/interfaces";
import { Image, Button, Empty, Typography } from "antd";
import {
  FilePdfOutlined,
  DownloadOutlined,
  FileImageOutlined,
} from "@ant-design/icons";
import { Grid, Paper, Box } from "@mui/material";

const { Text } = Typography;

export interface EquipmentDocumentsViewProps {
  documents: EquipmentDocumentI[];
}

export const EquipmentDocumentsView: React.FC<EquipmentDocumentsViewProps> = ({
  documents,
}) => {
  const formatFileSize = (bytes: number = 0) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
  };

  const handleDownload = (doc: EquipmentDocumentI) => {
    if (doc.src) {
      const link = document.createElement("a");
      link.href = doc.src;
      link.download = doc.fileName || `documento_${doc.order}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  // Filtrar solo documentos que tienen contenido
  const validDocuments = documents?.filter((doc) => doc.src && doc.src !== "") || [];

  if (validDocuments.length === 0) {
    return (
      <Box sx={{ p: 3, display: "flex", justifyContent: "center" }}>
        <Empty
          description="No hay documentos cargados para este activo"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      </Box>
    );
  }

  return (
    <Grid container spacing={2} sx={{ p: 2 }}>
      {validDocuments.map((doc, index) => (
        <Grid item xs={12} sm={6} md={4} key={doc.uuid || index}>
          <Paper
            elevation={2}
            sx={{
              p: 2,
              height: "100%",
              display: "flex",
              flexDirection: "column",
              transition: "transform 0.2s, box-shadow 0.2s",
              "&:hover": {
                transform: "translateY(-4px)",
                boxShadow: 4,
              },
            }}
          >
            {/* Preview del documento */}
            <Box
              sx={{
                width: "100%",
                height: 180,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: "#f5f5f5",
                borderRadius: 1,
                mb: 2,
                overflow: "hidden",
                border: "1px solid #e0e0e0",
              }}
            >
              {doc.fileType === "image" ? (
                <Image
                  width="100%"
                  height={180}
                  src={doc.src}
                  style={{ objectFit: "cover" }}
                  preview={{
                    mask: (
                      <div style={{ display: "flex", gap: 8 }}>
                        <FileImageOutlined style={{ fontSize: 20 }} />
                        Ver imagen
                      </div>
                    ),
                  }}
                />
              ) : doc.fileType === "pdf" ? (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <FilePdfOutlined
                    style={{ fontSize: 64, color: "#d32f2f" }}
                  />
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Documento PDF
                  </Text>
                </Box>
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <FilePdfOutlined
                    style={{ fontSize: 64, color: "#666" }}
                  />
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Documento
                  </Text>
                </Box>
              )}
            </Box>

            {/* Información del documento */}
            <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
              {doc.fileName && (
                <Text
                  strong
                  ellipsis={{ tooltip: doc.fileName }}
                  style={{ marginBottom: 8, fontSize: 14 }}
                >
                  {doc.fileName}
                </Text>
              )}

              {doc.observation && (
                <Text
                  type="secondary"
                  ellipsis={{ tooltip: doc.observation}}
                  style={{ marginBottom: 8, fontSize: 12 }}
                >
                  {doc.observation}
                </Text>
              )}

              {doc.fileSize && (
                <Text type="secondary" style={{ fontSize: 11, marginBottom: 8 }}>
                  Tamaño: {formatFileSize(doc.fileSize)}
                </Text>
              )}

              <Box sx={{ mt: "auto", pt: 1 }}>
                <Button
                  type="primary"
                  icon={<DownloadOutlined />}
                  onClick={() => handleDownload(doc)}
                  block
                  size="small"
                >
                  Descargar
                </Button>
              </Box>
            </Box>
          </Paper>
        </Grid>
      ))}
    </Grid>
  );
};